import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  IconButton,
  Button,
  Pagination,
  Stack,
  Badge,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  FormLabel,
  SelectChangeEvent,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Snackbar,
} from '@mui/material';
import {
  Mail as MailIcon,
  Add as AddIcon,
  Person as PersonIcon,
  Support as SupportIcon,
  Circle as CircleIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  IndeterminateCheckBox as IndeterminateCheckBoxIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';
import { formatDistanceToNow } from 'date-fns';
import NewMessageModal from '../../components/messages/NewMessageModal';
import { useMessagePolling } from '../../hooks/useMessagePolling';
import { brandColors } from '../../theme';

interface MessageThread {
  id: number;
  subject: string;
  category: string | null;
  status: 'open' | 'pending' | 'resolved' | 'closed';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  lastMessage: {
    content: string;
    senderType: 'user' | 'admin';
    createdAt: string;
  } | null;
  unreadCount: number;
  createdAt: string;
  updatedAt: string;
  isArchived?: boolean;
}

interface ThreadsResponse {
  success: boolean;
  data?: {
    threads: MessageThread[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

const MessagesPage: React.FC = () => {
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [page, setPage] = useState(1);
  const [statusFilters, setStatusFilters] = useState<string[]>(['open', 'pending', 'resolved', 'closed']);
  const [sortBy, setSortBy] = useState<'latest' | 'unread'>('latest');
  const [newMessageModalOpen, setNewMessageModalOpen] = useState(false);
  const [selectedThreads, setSelectedThreads] = useState<number[]>([]);
  const [archiveSnackbar, setArchiveSnackbar] = useState(false);
  const [archiveMessage, setArchiveMessage] = useState('');

  // Enable polling for this page
  useMessagePolling({
    enabled: true,
    interval: 30000, // 30 seconds for thread list
    onMessagesPage: true,
  });

  // Fetch threads
  const { data: threadsData, isLoading, error, refetch } = useQuery({
    queryKey: ['messages', 'threads', page, statusFilters, sortBy],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        sort: sortBy,
      });
      
      // Check if archived is included in filters
      const includeArchived = statusFilters.includes('archived');
      
      // Add status filters (excluding 'archived' as it's not a real status)
      const actualStatuses = statusFilters.filter(s => s !== 'archived');
      if (actualStatuses.length > 0 && actualStatuses.length < 4) {
        actualStatuses.forEach(status => params.append('status', status));
      }
      
      // Add archived flag if needed
      if (includeArchived) {
        params.append('includeArchived', 'true');
      }

      const response = await authenticatedFetch(
        `${API_URL}/api/store/messages/list?${params}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response?.ok) {
        throw new Error('Failed to fetch messages');
      }

      const data: ThreadsResponse = await response.json();
      return data;
    },
    staleTime: 10000, // Consider data stale after 10 seconds
  });

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    setSelectedThreads([]); // Clear selection on page change
  };

  const handleStatusFilterToggle = (status: string) => {
    setStatusFilters(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
    setPage(1); // Reset to first page
  };

  const handleSelectAll = () => {
    if (selectedThreads.length === threads.length) {
      setSelectedThreads([]);
    } else {
      setSelectedThreads(threads.map(t => t.id));
    }
  };

  const handleSelectThread = (threadId: number) => {
    setSelectedThreads(prev => 
      prev.includes(threadId)
        ? prev.filter(id => id !== threadId)
        : [...prev, threadId]
    );
  };

  const handleArchiveSelected = async (archive: boolean) => {
    if (selectedThreads.length === 0) return;

    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/store/messages/archive`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            threadIds: selectedThreads,
            archive: archive
          })
        }
      );

      if (response?.ok) {
        const result = await response.json();
        setArchiveMessage(
          archive 
            ? `${result.data.affectedCount} message${result.data.affectedCount !== 1 ? 's' : ''} archived`
            : `${result.data.affectedCount} message${result.data.affectedCount !== 1 ? 's' : ''} unarchived`
        );
        setArchiveSnackbar(true);
        setSelectedThreads([]);
        refetch();
      } else {
        setArchiveMessage('Failed to archive messages');
        setArchiveSnackbar(true);
      }
    } catch (error) {
      setArchiveMessage('Failed to archive messages');
      setArchiveSnackbar(true);
    }
  };

  const handleSortChange = (event: SelectChangeEvent) => {
    setSortBy(event.target.value as 'latest' | 'unread');
    setPage(1); // Reset to first page
  };

  const handleThreadClick = (threadId: number, event: React.MouseEvent) => {
    // Don't navigate if clicking on checkbox
    if ((event.target as HTMLElement).closest('.MuiCheckbox-root')) {
      return;
    }
    navigate(`/messages/${threadId}`);
  };

  const handleNewMessage = () => {
    setNewMessageModalOpen(true);
  };

  const handleNewMessageClose = () => {
    setNewMessageModalOpen(false);
    refetch(); // Refresh thread list
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'info';
      case 'pending':
        return 'warning';
      case 'resolved':
        return 'success';
      case 'closed':
        return 'default';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'normal':
        return 'primary';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  const getCategoryIcon = (category: string | null) => {
    switch (category) {
      case 'support':
        return <SupportIcon />;
      case 'billing':
        return <MailIcon />;
      case 'technical':
        return <SupportIcon />;
      default:
        return <MailIcon />;
    }
  };

  const truncateContent = (content: string, maxLength: number = 100) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  const threads = threadsData?.data?.threads || [];
  const pagination = threadsData?.data?.pagination;

  return (
    <Box sx={{ 
      backgroundColor: brandColors.white, 
      minHeight: '100vh', 
      marginTop: '-64px', 
      paddingTop: '64px',
      marginLeft: 'calc(-50vw + 50%)',
      marginRight: 'calc(-50vw + 50%)',
      paddingLeft: 'calc(50vw - 50%)',
      paddingRight: 'calc(50vw - 50%)',
    }}>
      {/* Hero/Header */}
      <Box sx={{ 
        background: `linear-gradient(to bottom, ${brandColors.white}, ${brandColors.lightGray})`, 
        pb: { xs: 4, md: 6 },
        marginLeft: 'calc(-50vw + 50%)',
        marginRight: 'calc(-50vw + 50%)',
        paddingLeft: 'calc(50vw - 50%)',
        paddingRight: 'calc(50vw - 50%)',
      }}>
        <Container maxWidth="lg" sx={{ pt: 4 }}>
          <Typography
            component="h1"
            sx={{
              color: brandColors.blue,
              fontWeight: 400,
              fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3.25rem' },
              lineHeight: 1.05,
              letterSpacing: '-0.5px',
              mb: 2,
            }}
          >
            Messages
          </Typography>
          <Typography
            variant="h6"
            sx={{ color: brandColors.mediumGray, fontWeight: 400, maxWidth: 900 }}
          >
            View and manage your conversations with our support team
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 3 }}>

      {/* Filters and Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={3} alignItems="center" sx={{ flexGrow: 1 }}>
            <FormControl component="fieldset" size="small">
              <FormLabel component="legend" sx={{ fontSize: '0.875rem' }}>Status</FormLabel>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={statusFilters.includes('open')}
                      onChange={() => handleStatusFilterToggle('open')}
                      size="small"
                    />
                  }
                  label="Open"
                  sx={{ mr: 1 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={statusFilters.includes('pending')}
                      onChange={() => handleStatusFilterToggle('pending')}
                      size="small"
                    />
                  }
                  label="Pending"
                  sx={{ mr: 1 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={statusFilters.includes('resolved')}
                      onChange={() => handleStatusFilterToggle('resolved')}
                      size="small"
                    />
                  }
                  label="Resolved"
                  sx={{ mr: 1 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={statusFilters.includes('closed')}
                      onChange={() => handleStatusFilterToggle('closed')}
                      size="small"
                    />
                  }
                  label="Closed"
                  sx={{ mr: 1 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={statusFilters.includes('archived')}
                      onChange={() => handleStatusFilterToggle('archived')}
                      size="small"
                    />
                  }
                  label="Archived"
                />
              </FormGroup>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 0.5 }}>Sort By</FormLabel>
              <Select
                value={sortBy}
                onChange={handleSortChange}
                sx={{ height: 32 }}
              >
                <MenuItem value="latest">Latest</MenuItem>
                <MenuItem value="unread">Unread</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNewMessage}
          >
            New Message
          </Button>
        </Stack>
        
        {/* Bulk Actions */}
        {selectedThreads.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {selectedThreads.length} selected
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ArchiveIcon />}
              onClick={() => handleArchiveSelected(true)}
            >
              Archive
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<UnarchiveIcon />}
              onClick={() => handleArchiveSelected(false)}
            >
              Unarchive
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => setSelectedThreads([])}
            >
              Clear Selection
            </Button>
          </Box>
        )}
      </Paper>

      {/* Thread List */}
      <Paper>
        {isLoading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 4 }}>
            <Alert severity="error">
              Failed to load messages. Please try again later.
            </Alert>
          </Box>
        ) : threads.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No messages found
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleNewMessage}
              sx={{ mt: 2 }}
            >
              Start a Conversation
            </Button>
          </Box>
        ) : (
          <>
            {/* Select All Checkbox */}
            <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedThreads.length === threads.length}
                      indeterminate={selectedThreads.length > 0 && selectedThreads.length < threads.length}
                      onChange={handleSelectAll}
                      icon={<CheckBoxOutlineBlankIcon />}
                      checkedIcon={<CheckBoxIcon />}
                      indeterminateIcon={<IndeterminateCheckBoxIcon />}
                    />
                  }
                  label="Select All"
                />
            </Box>
            <List sx={{ p: 0 }}>
              {threads.map((thread, index) => (
                <ListItem
                  key={thread.id}
                  button
                  onClick={(e) => handleThreadClick(thread.id, e)}
                  divider={index < threads.length - 1}
                  sx={{
                    '&:hover': {
                      backgroundColor: thread.isArchived ? 'grey.200' : 'action.hover',
                    },
                    backgroundColor: selectedThreads.includes(thread.id) 
                      ? 'action.disabledBackground'
                      : thread.isArchived 
                        ? 'grey.100'
                        : thread.unreadCount > 0 
                          ? 'action.selected' 
                          : 'inherit',
                    opacity: thread.isArchived ? 0.85 : 1,
                  }}
                >
                  <Checkbox
                    checked={selectedThreads.includes(thread.id)}
                    onChange={() => handleSelectThread(thread.id)}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ mr: 1 }}
                  />
                  <ListItemAvatar>
                  <Badge
                    badgeContent={thread.unreadCount}
                    color="error"
                    invisible={thread.unreadCount === 0}
                  >
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      {getCategoryIcon(thread.category)}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>
                
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography 
                        variant="subtitle1" 
                        component="span"
                        sx={{ color: thread.isArchived ? 'text.secondary' : 'text.primary' }}
                      >
                        {thread.subject}
                      </Typography>
                      <Chip
                        label={thread.status}
                        size="small"
                        color={getStatusColor(thread.status) as any}
                      />
                      {thread.priority !== 'normal' && (
                        <Chip
                          label={thread.priority}
                          size="small"
                          color={getPriorityColor(thread.priority) as any}
                          variant="outlined"
                        />
                      )}
                      {thread.isArchived && (
                        <Chip
                          label="Archived"
                          size="small"
                          variant="filled"
                          color="default"
                          icon={<ArchiveIcon sx={{ fontSize: 16 }} />}
                          sx={{ 
                            backgroundColor: 'grey.300',
                            '& .MuiChip-icon': { color: 'grey.600' }
                          }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      {thread.lastMessage && (
                        <Typography
                          variant="body2"
                          color={thread.isArchived ? "text.disabled" : "text.secondary"}
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            gap: 0.5,
                            mt: 0.5 
                          }}
                        >
                          {thread.lastMessage.senderType === 'admin' && (
                            <Chip
                              label="Support"
                              size="small"
                              sx={{ height: 16, fontSize: '0.7rem' }}
                            />
                          )}
                          {truncateContent(thread.lastMessage.content)}
                        </Typography>
                      )}
                      <Typography 
                        variant="caption" 
                        color={thread.isArchived ? "text.disabled" : "text.disabled"}
                        sx={{ opacity: thread.isArchived ? 0.7 : 1 }}
                      >
                        {formatDistanceToNow(new Date(thread.updatedAt), { addSuffix: true })}
                      </Typography>
                    </Box>
                  }
                />
                
                {thread.unreadCount > 0 && (
                  <CircleIcon sx={{ color: 'error.main', fontSize: 12 }} />
                )}
              </ListItem>
            ))}
          </List>
          </>
        )}

        {/* Pagination */}
        {!isLoading && !error && pagination && pagination.pages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Pagination
              count={pagination.pages}
              page={page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}
      </Paper>

      {/* New Message Modal */}
      <NewMessageModal
        open={newMessageModalOpen}
        onClose={handleNewMessageClose}
      />
      
      {/* Archive Snackbar */}
      <Snackbar
        open={archiveSnackbar}
        autoHideDuration={3000}
        onClose={() => setArchiveSnackbar(false)}
        message={archiveMessage}
      />
      </Container>
    </Box>
  );
};

export default MessagesPage;